import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatCurrency, relTime } from "@/lib/api";
import PageHeader from "@/components/layout/PageHeader";
import { PIPELINE } from "@/constants/testIds";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Search, ListTodo, AlertCircle } from "lucide-react";
import ChannelIcon, { CHANNEL_COLORS } from "../components/ChannelIcon";
import ContactAvatar from "../components/ContactAvatar";
import DateRangeFilter from "../components/DateRangeFilter";

export default function Pipeline() {
  const qc = useQueryClient();
  const { data: stages = [] } = useQuery({ queryKey: ["stages"], queryFn: () => api.get("/pipeline/stages").then((r) => r.data) });
  const [range, setRange] = useState({ from: null, to: null, preset: null });
  const [search, setSearch] = useState("");
  const { data: deals = [] } = useQuery({
    queryKey: ["deals", range.from, range.to, search],
    queryFn: () => api.get("/deals", { params: { q: search || undefined, date_from: range.from || undefined, date_to: range.to || undefined } }).then((r) => r.data),
    refetchInterval: 15000,
  });
  const { data: taskCounts = {} } = useQuery({
    queryKey: ["deals-task-counts"],
    queryFn: () => api.get("/deals/task-counts").then((r) => r.data),
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
  });
  const [dragId, setDragId] = useState(null);
  const [showAddStage, setShowAddStage] = useState(false);

  const moveMut = useMutation({
    mutationFn: ({ dealId, stageId }) => api.post(`/deals/${dealId}/move`, { stage_id: stageId }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["deals"] }); toast.success("Deal moved"); },
  });

  const handleDrop = (stageId) => {
    if (dragId) moveMut.mutate({ dealId: dragId, stageId });
    setDragId(null);
  };

  return (
    <div>
      <PageHeader
        overline="Sales"
        title="Pipeline"
        subtitle={`${deals.length} deals in ${stages.length} stages`}
        actions={
          <Dialog open={showAddStage} onOpenChange={setShowAddStage}>
            <DialogTrigger asChild>
              <Button data-testid={PIPELINE.addStageButton} className="rounded-sm bg-primary hover:bg-primary/90"><Plus className="h-4 w-4 mr-2" />Add Stage</Button>
            </DialogTrigger>
            <AddStageDialog onClose={() => setShowAddStage(false)} nextOrder={stages.length} />
          </Dialog>
        }
      />

      <div className="px-6 lg:px-10 pt-6 pb-10">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input data-testid="pipeline-search" placeholder="Search deals, contacts, tags…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 rounded-sm bg-secondary/40" />
          </div>
          <DateRangeFilter value={range} onChange={setRange} testId="pipeline-date" />
        </div>
        <div className="flex gap-4 overflow-x-auto pb-6 items-start snap-x kanban-scroll">
          {stages.map((s) => {
            const stageDeals = deals.filter((d) => d.stage_id === s.id);
            const total = stageDeals.reduce((a, b) => a + (b.value || 0), 0);
            return (
              <div
                key={s.id}
                data-testid={PIPELINE.column}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleDrop(s.id)}
                className="min-w-[300px] w-[300px] snap-start flex-none"
              >
                <div className="flex items-center justify-between px-2 mb-3 sticky top-0">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">{s.is_won ? "Won" : s.is_lost ? "Lost" : "Stage"}</div>
                    <div className="font-display text-base font-extrabold">{s.name}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-muted-foreground">{stageDeals.length} deals</div>
                    <div className="text-sm font-semibold">{formatCurrency(total)}</div>
                  </div>
                </div>
                <div className={`space-y-2 border border-border rounded-sm p-2 min-h-[200px] ${s.is_won ? "bg-green-500/5" : s.is_lost ? "bg-red-500/5" : "bg-secondary/20"}`}>
                  {stageDeals.map((d) => (
                    <DealCard key={d.id} d={d} setDragId={setDragId} taskCounts={taskCounts[d.id]} />
                  ))}
                  {stageDeals.length === 0 && <div className="text-xs text-muted-foreground text-center py-8">Drop deals here</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function DealCard({ d, setDragId, taskCounts }) {
  const daysInStage = d.stage_updated_at ? Math.floor((Date.now() - new Date(d.stage_updated_at).getTime()) / 86400000) : 0;
  const sourceChannel = d.source_channel || d.contact?.source_channel;
  const brand = sourceChannel ? CHANNEL_COLORS[sourceChannel] : null;
  const tc = taskCounts || null;
  return (
    <Link
      to={`/deals/${d.id}`}
      draggable
      onDragStart={() => setDragId(d.id)}
      data-testid={PIPELINE.card}
      className="block bg-card border border-border rounded-sm p-3 hover:border-primary/60 transition-all card-in group relative overflow-hidden"
    >
      {brand && (
        <div className="absolute top-0 left-0 h-full w-0.5" style={{ backgroundColor: brand.color, opacity: 0.7 }} />
      )}
      <div className="flex items-start justify-between mb-2 pl-1">
        <div className="font-semibold text-sm truncate flex-1">{d.name}</div>
        <div className="text-xs font-bold text-primary flex-none ml-2">{formatCurrency(d.value, d.currency)}</div>
      </div>
      <div className="flex items-center gap-2 mb-2 pl-1">
        <div className="relative flex-none">
          <ContactAvatar src={d.contact?.avatar_url} name={d.contact?.name} size={22} />
          {sourceChannel && (
            <span className="absolute -bottom-1 -right-1">
              <ChannelIcon channel={sourceChannel} size={8} badge />
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground truncate">{d.contact?.name}{d.contact?.company_name ? ` · ${d.contact.company_name}` : ""}</div>
      </div>
      <div className="flex flex-wrap gap-1 mb-2 pl-1">
        {(d.tags || []).map((t) => <Badge key={t} variant="secondary" className="rounded-sm text-[10px] px-1.5 py-0">{t}</Badge>)}
      </div>
      <div className="flex items-center justify-between text-[10px] text-muted-foreground pl-1 gap-2">
        <span className="flex items-center gap-2">
          <span>{daysInStage}d in stage</span>
          {tc && tc.open > 0 && (
            <span
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border text-[10px] font-bold ${tc.overdue > 0 ? "bg-red-500/15 text-red-400 border-red-500/40" : "bg-primary/15 text-primary border-primary/40"}`}
              title={`${tc.open} open task${tc.open === 1 ? "" : "s"}${tc.overdue > 0 ? ` · ${tc.overdue} overdue` : ""}`}
              data-testid={`deal-tasks-badge-${d.id}`}
            >
              {tc.overdue > 0 ? <AlertCircle className="h-3 w-3" /> : <ListTodo className="h-3 w-3" />}
              {tc.open}{tc.overdue > 0 ? ` (${tc.overdue}!)` : ""}
            </span>
          )}
        </span>
        {d.assigned_agent?.name && <span className="truncate">{d.assigned_agent.name}</span>}
      </div>
    </Link>
  );
}

function AddStageDialog({ onClose, nextOrder }) {
  const [name, setName] = useState("");
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => api.post("/pipeline/stages", { name, order: nextOrder, is_won: false, is_lost: false }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["stages"] }); toast.success("Stage added"); onClose(); },
  });
  return (
    <DialogContent className="rounded-sm">
      <DialogHeader><DialogTitle>Add Pipeline Stage</DialogTitle></DialogHeader>
      <div><Label>Stage name</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Demo Scheduled" /></div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
        <Button className="rounded-sm bg-primary" onClick={() => mut.mutate()} disabled={!name || mut.isPending}>Add</Button>
      </DialogFooter>
    </DialogContent>
  );
}
